import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {BehaviorSubject, Observable, of, Subject} from "rxjs";
import {catchError, switchMap} from "rxjs/operators";
import {environment} from "../../../../environments/environment";
import {Injectable} from "@angular/core";
import {IMainStats} from "./interfaces/main-stats.interface";
import {ITopBalances} from "./interfaces/top-balances.interface";
import {INewUsers} from "./interfaces/new-users.interface";
import {ITopWithdraws} from "./interfaces/top-withdraws.interface";
import {IOnline} from "./interfaces/online.interface";
import {SnackBarService} from "../../../@core/services/snack-bar.service";
import {ITopIncome} from "./interfaces/get-top-incomes.interface";
import {IExcludedUsers} from "./interfaces/excluded-users.interface";
import {ITopTransactions, ITopTransactionsResult, ITransactionType} from "./interfaces/top-transactions.interface";
import {IMoneyIncome} from "./interfaces/money-income.interface";

@Injectable(
  {
    providedIn: 'root'
  }
)
export class StatsService {

  private readonly mainStatsRequest: Subject<void> = new Subject<void>();
  private readonly mainStats: BehaviorSubject<IMainStats> = new BehaviorSubject<any>(null);
  public readonly mainStats$: Observable<IMainStats> = this.mainStats.asObservable();

  private readonly newUsersRequest: Subject<any> = new Subject<any>();
  private readonly newUsers: BehaviorSubject<INewUsers[]> = new BehaviorSubject<any>(null);
  public readonly newUsers$: Observable<INewUsers[]> = this.newUsers.asObservable();

  private readonly topBalancesRequest: Subject<void> = new Subject<void>();
  private readonly topBalances: BehaviorSubject<ITopBalances[]> = new BehaviorSubject<any>(null);
  public readonly topBalances$: Observable<ITopBalances[]> = this.topBalances.asObservable();

  private readonly topWithdrawsRequest: Subject<any> = new Subject<any>();
  private readonly topWithdraws: BehaviorSubject<ITopWithdraws[]> = new BehaviorSubject<any>(null);
  public readonly topWithdraws$: Observable<ITopWithdraws[]> = this.topWithdraws.asObservable();

  private readonly paymentStatsRequest: Subject<any> = new Subject<any>();
  private readonly paymentStats: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public readonly paymentStats$: Observable<any> = this.paymentStats.asObservable();

  private readonly onlineRequest: Subject<void> = new Subject<void>();
  private readonly online: Subject<IOnline> = new Subject<IOnline>();
  public readonly online$: Observable<IOnline> = this.online.asObservable();

  private readonly topRefIncomesRequest: Subject<any> = new Subject<any>();
  private readonly topRefIncomes: Subject<ITopIncome[]> = new Subject<ITopIncome[]>();
  public readonly topRefIncomes$: Observable<ITopIncome[]> = this.topRefIncomes.asObservable();

  private readonly excludedUsersRequest: Subject<void> = new Subject<void>();
  private readonly excludedUsers: Subject<IExcludedUsers[]> = new Subject<IExcludedUsers[]>();
  public readonly excludedUsers$: Observable<IExcludedUsers[]> = this.excludedUsers.asObservable();

  private readonly topWithdrawalsReportRequest: Subject<any> = new Subject<any>();
  private readonly topWithdrawalsReport: Subject<any> = new Subject<any>();
  public readonly topWithdrawalsReport$: Observable<any> = this.topWithdrawalsReport.asObservable();


  constructor(
    private readonly http: HttpClient,
    private readonly sb: SnackBarService
  ) {
    this.subscribeEmitters();
  }

  public requestMainStats(dateForm: any): void {
    this.mainStatsRequest.next(dateForm);
  }

  public requestNewUsers(dateForm: any, pageSize: number, currentPage: number): void {
    this.newUsersRequest.next({dateForm, pageSize, currentPage});
  }

  public requestTopBalances(): void {
    this.topBalancesRequest.next();
  }

  public requestWithdraws(dateForm: any, pageSize: number, currentPage: number): void {
    this.topWithdrawsRequest.next({dateForm, pageSize, currentPage});
  }

  public requestPaymentStats(dateForm: any, pageSize: number, currentPage: number): void {
    this.paymentStatsRequest.next({dateForm, pageSize, currentPage});
  }

  public requestTopWithdrawalsReport(dateForm: any): void {
    this.topWithdrawalsReportRequest.next(dateForm);
  }

  public downloadTopWithdrawalsReport(selectedDate: any): Observable<any> {
    return this.http.get(`${environment.GATEWAY_URL}/stats/download-withdrawal-report?dateFrom=${selectedDate.start}&dateTo=${selectedDate.end}`, {
      responseType: "blob",
      withCredentials: true
    }).pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)))
  }

  public downloadReport(selectedDate: any): Observable<any> {
    return this.http.get(`${environment.GATEWAY_URL}/stats/excel?startDate=${selectedDate.start}&endDate=${selectedDate.end}`, {
      responseType: "blob",
      withCredentials: true
    })
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)))
  }

  public requestOnline(): void {
    this.onlineRequest.next();
  }

  public requestTopRefIncomes(dateForm: { start: string, end: string }, taken: boolean): void {
    this.topRefIncomesRequest.next({dateForm, taken});
  }

  public requestExcludedUsers(): void {
    this.excludedUsersRequest.next();
  }

  public excludeUserFromStat(userId: number, exclude: boolean): Observable<any> {
    return this.http.post(`${environment.GATEWAY_URL}/stats/exclude-list`, {userId, exclude})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public getTopTransactionTypes(): Observable<ITransactionType[]> {
    return this.http.get<ITransactionType[]>(`${environment.GATEWAY_URL}/stats/top-transactions`)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public getTopTransactionsByType(params: {
    type?: number | null;
    dateForm?: { start?: string; end?: string };
    pageSize: number;
    currentPage: number;
  }): Observable<ITopTransactionsResult> {
    let url = `${environment.GATEWAY_URL}/stats/top-transactions-data?page[size]=${params.pageSize}&page[number]=${params.currentPage + 1}`;
    if (params.type != null) {
      url += `&type=${params.type}`;
    }
    if (params.dateForm?.start) {
      url += `&startDate=${params.dateForm.start}`;
    }
    if (params.dateForm?.end) {
      url += `&endDate=${params.dateForm.end}`;
    }
    return this.http.get<ITopTransactionsResult>(url)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public getMoneyIncome(): Observable<IMoneyIncome[]> {
    return this.http.get<any>(`${environment.GATEWAY_URL}/stats/money-income`)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  private subscribeEmitters(): void {

    this.mainStatsRequest.pipe(
      switchMap((dateForm: any) => this.http.get(`${environment.GATEWAY_URL}/stats/main?startDate=${dateForm.start}&endDate=${dateForm.end}`, {withCredentials: true}).pipe(
        catchError((error: HttpErrorResponse) => of(null)),
      )),
    ).subscribe(
      (result: any) => this.mainStats.next(result),
    );

    this.newUsersRequest.pipe(
      switchMap((params) => this.http.get(`${environment.GATEWAY_URL}/stats/new-users?startDate=${params.dateForm.start}&endDate=${params.dateForm.end}&page[size]=${params.pageSize}&page[number]=${params.currentPage + 1}`, {withCredentials: true}).pipe(
        catchError((error: HttpErrorResponse) => of(null)),
      )),
    ).subscribe(
      (result: any) => this.newUsers.next(result),
    );

    this.topBalancesRequest.pipe(
      switchMap(() => this.http.get(`${environment.GATEWAY_URL}/stats/top-money`, {withCredentials: true}).pipe(
        catchError((error: HttpErrorResponse) => of(null)),
      )),
    ).subscribe(
      (result: any) => this.topBalances.next(result),
    );

    this.topWithdrawsRequest.pipe(
      switchMap((params) => this.http.get(`${environment.GATEWAY_URL}/stats/top-withdraws?startDate=${params.dateForm.start}&endDate=${params.dateForm.end}&page[size]=${params.pageSize}&page[number]=${params.currentPage + 1}`, {withCredentials: true})
        .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)))))
      .subscribe(
        (result: any) => this.topWithdraws.next(result));

    this.paymentStatsRequest.pipe(
      switchMap((params) => this.http.get(`${environment.GATEWAY_URL}/stats/payment-stats?startDate=${params.dateForm.start}&endDate=${params.dateForm.end}&page[size]=${params.pageSize}&page[number]=${params.currentPage + 1}`, {withCredentials: true})
        .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)))))
      .subscribe((result: any) => this.paymentStats.next(result));

    this.onlineRequest.pipe(
      switchMap(() => this.http.get<IOnline>(`${environment.GATEWAY_URL}/online`, {withCredentials: true})
        .pipe(
          catchError((error: HttpErrorResponse) => this.onHandleError(error))
        )),
    ).subscribe((online: any) => this.online.next(online));

    this.topRefIncomesRequest.pipe(
      switchMap((params: any) => this.http.get(`${environment.GATEWAY_URL}/referral/top-incomes?dateFrom=${params.dateForm.start}&dateTo=${params.dateForm.end}&taken=${params.taken}`, {withCredentials: true}).pipe(
        catchError((error: HttpErrorResponse) => this.onHandleError(error)))),
    ).subscribe(
      (topRefIncomes: any) => this.topRefIncomes.next(topRefIncomes),
    );

    this.excludedUsersRequest.pipe(
      switchMap(() => this.http.get(`${environment.GATEWAY_URL}/stats/exclude-list`, {withCredentials: true})
        .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)))),
    ).subscribe((excludedUsers: any) => this.excludedUsers.next(excludedUsers));

    this.topWithdrawalsReportRequest.pipe(
      switchMap((params) => this.http.get(`${environment.GATEWAY_URL}/stats/top-withdraws-details?dateFrom=${params.start}&dateTo=${params.end}`, {withCredentials: true})
        .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)))))
      .subscribe((result: any) => this.topWithdrawalsReport.next(result));
  }

  private onHandleError(error: HttpErrorResponse): Observable<any> {
    const message = error.error.statusCode + ' ' + (error.error.message || error.error.error);
    this.sb.openSnackBar(message, 3000, 'error');
    return of();
  }
}
